import { useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Pencil,
  Plus,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Empty, EmptyDescription, EmptyTitle } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Communicator } from '../tools/Communication';
import { centsToPrice } from '@/utils/formatter';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Currency } from '@/utils/price';
import {
  expandNamesByLanguage,
  UserLanguage,
  IdByUserLanguage,
  getDefaultNamesByLanguageObject,
} from '@/utils/translation';

interface MerchantOrderItemOption {
  typeId: number;
  name: string | null;
  resupplyOptions: number[];
  resupplyDisabled: boolean | null;
  pickupDisabled: boolean | null;
  resupplyDisabledUntil: string | null;
  orderKey: number;
  namesByLanguage: { [x: string]: string };
  internalName: string;
  purchasePriceCents: string;
}

// Selectable resupply quantities: 0–9, then 10→100 in steps of 5,
// 110→300 in steps of 10, then 350+ in steps of 50.
const RESUPPLY_OPTIONS = Array.from({ length: 63 }, (_, i) => {
  if (i < 10) return i;
  if (i < 29) return 10 + (i - 10) * 5;
  if (i < 49) return 100 + (i - 28) * 10;
  return 300 + (i - 48) * 50;
});

type StatusFilter = 'active' | 'inactive' | 'all';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString(
    i18n.language,
    getDateFormatOptions(),
  );
}

/** True while a resupply pause is still in effect (until date is today or later). */
function isPauseActive(until: string | null): boolean {
  if (!until) return false;
  const untilDate = new Date(until);
  if (isNaN(untilDate.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  untilDate.setHours(0, 0, 0, 0);
  return untilDate >= today;
}

/** An option counts as active when resupply is on and not currently paused. */
function isOptionActive(opt: MerchantOrderItemOption): boolean {
  return !opt.resupplyDisabled && !isPauseActive(opt.resupplyDisabledUntil);
}

/** Case-insensitive subsequence match — the query characters appear in order. */
function fuzzyMatch(query: string, text: string): boolean {
  const needle = query.toLowerCase();
  const haystack = text.toLowerCase();
  let i = 0;
  for (const char of haystack) {
    if (char === needle[i]) i++;
    if (i === needle.length) return true;
  }
  return i === needle.length;
}

/** Fuzzy match on the name, exact match on the id. */
function matchesSearch(opt: MerchantOrderItemOption, query: string): boolean {
  const q = query.trim();
  if (q === '') return true;
  if (String(opt.typeId) === q) return true;
  return fuzzyMatch(q, opt.name ?? '');
}

/**
 * Communicator rejects with the raw Response, so pull the ApiErrorResponse message out
 * of it — a failed save is otherwise reported as an unhelpful generic error.
 */
async function readApiErrorMessage(
  error: unknown,
): Promise<string | undefined> {
  if (!(error instanceof Response)) return undefined;
  try {
    const body = await error.json();
    return body?.errorMessage ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * `Name` is [Required] on the API model, but a container type is allowed to have no
 * name at all (ContainerTypeEntity inherits a plain, nullable Name from Translatable)
 * and this page loads every container type via includeAllContainers. Since all
 * options are posted as one array, a single nameless container type would fail model
 * validation for the whole payload and block saving anything at all — so fall back
 * to a placeholder. The value is discarded for container options anyway: the backend
 * only persists Name for TypeId < 1 and otherwise rewrites it from the container type.
 */
function nameForPayload(opt: MerchantOrderItemOption): string {
  const candidates = [opt.name, opt.internalName];
  const name = candidates.find((value) => value != null && value.trim() !== '');
  return name ?? `#${opt.typeId}`;
}

/**
 * True when at least one display name deviates from the internal name, i.e. it was
 * translated by hand instead of being filled in from the fallback.
 */
function hasCustomDisplayNames(opt: MerchantOrderItemOption): boolean {
  if (opt.namesByLanguage == null) return false;
  return Object.keys(UserLanguage).some(
    (lang) =>
      opt.namesByLanguage[lang] != null &&
      opt.namesByLanguage[lang] !== opt.internalName,
  );
}

export function EditMerchantOrderItemOptionView() {
  const [options, setOptions] = useState<MerchantOrderItemOption[]>([]);
  const [containerTypeClassesById, setContainerTypeClassesById] = useState<{
    [id: number]: string;
  }>({});
  const [savedSnapshot, setSavedSnapshot] = useState('');
  const [editingOption, setEditingOption] =
    useState<MerchantOrderItemOption | null>(null);
  const [namesOpen, setNamesOpen] = useState(false);
  const [loading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [customQuantity, setCustomQuantity] = useState('');

  async function fetchOptions(): Promise<void> {
    const response = await Communicator.Get(
      '/api/3/MerchantOrder/GetOrderItemOptions',
      { includeAllContainers: true },
    );
    const data = await response.json();
    const expanded = data.map((e: MerchantOrderItemOption) =>
      expandNamesByLanguage(e),
    ) as MerchantOrderItemOption[];
    setOptions(expanded);
    setSavedSnapshot(JSON.stringify(expanded));
  }

  useEffect(() => {
    Promise.all([
      fetchOptions(),
      Communicator.Get('/Container/GetAllContainerClassesById', {})
        .then((response) => response.json())
        .then((data) => setContainerTypeClassesById(data)),
    ]).finally(() => setIsLoading(false));
  }, []);

  const isDirty = useMemo(
    () => savedSnapshot !== '' && JSON.stringify(options) !== savedSnapshot,
    [options, savedSnapshot],
  );

  if (loading) {
    return (
      <div className="tw:flex tw:justify-center tw:py-24">
        <Spinner className="tw:size-8 tw:text-muted-foreground" />
      </div>
    );
  }

  function editOption(item: MerchantOrderItemOption): void {
    // Start expanded when the display names are not just copies of the internal
    // name — those are the values that actually show up in the apps, so hiding
    // them would make a rename look like it did nothing.
    setNamesOpen(hasCustomDisplayNames(item));
    setEditingOption({ ...item });
  }

  /**
   * The API returns the display name of the requested language as both `name` and
   * `internalName`, and reads it back from `namesByLanguage` too (see
   * Translatable.TranslateName) — so renaming has to carry over to every language
   * that still holds the old name, otherwise the change is invisible after saving.
   * Hand-translated names are left untouched.
   */
  function renameOption(newName: string): void {
    if (editingOption == null) return;
    const previousName = editingOption.internalName;
    const namesByLanguage = { ...editingOption.namesByLanguage };
    for (const lang of Object.keys(namesByLanguage)) {
      if (namesByLanguage[lang] === previousName) {
        namesByLanguage[lang] = newName;
      }
    }
    setEditingOption({
      ...editingOption,
      name: newName,
      internalName: newName,
      namesByLanguage,
    });
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  function confirmEdit(): void {
    if (editingOption == null) return;
    const sorted = [...editingOption.resupplyOptions].sort((a, b) => a - b);
    const updated = { ...editingOption, resupplyOptions: sorted };
    setOptions((prev) =>
      prev.map((opt) => (opt.typeId === updated.typeId ? updated : opt)),
    );
    setEditingOption(null);
  }

  function toggleResupplyOption(value: number): void {
    if (editingOption == null) return;
    const selected = editingOption.resupplyOptions.includes(value);
    setEditingOption({
      ...editingOption,
      resupplyOptions: selected
        ? editingOption.resupplyOptions.filter((i) => i !== value)
        : [...editingOption.resupplyOptions, value],
    });
  }

  /** Adds a quantity typed into the free input (e.g. a carton size not in the preset list). */
  function addCustomResupplyOption(): void {
    if (editingOption == null) return;
    const value = parseInt(customQuantity, 10);
    if (!Number.isInteger(value) || value < 0) return;
    if (!editingOption.resupplyOptions.includes(value)) {
      setEditingOption({
        ...editingOption,
        resupplyOptions: [...editingOption.resupplyOptions, value],
      });
    }
    setCustomQuantity('');
  }

  function addOption(): void {
    const typeId = Math.min(...options.map((opt) => opt.typeId)) - 1;
    const orderKey = Math.min(...options.map((opt) => opt.orderKey)) - 1;
    const newOption: MerchantOrderItemOption = {
      name: 'New Option',
      pickupDisabled: true,
      resupplyDisabled: false,
      resupplyDisabledUntil: null,
      resupplyOptions: [],
      typeId,
      orderKey,
      namesByLanguage: getDefaultNamesByLanguageObject('New Option'),
      internalName: 'New Option',
      purchasePriceCents: '0',
    };
    setOptions((prev) => [...prev, newOption]);
    setQuery('');
    editOption(newOption);
  }

  function setOrderKey(typeId: number, newOrderKey: number): void {
    if (
      // non-container options are kept below the containers (orderKey <= 0)
      (typeId < 1 && newOrderKey > 0) ||
      options.every((opt) => opt.orderKey > newOrderKey) ||
      options.every((opt) => opt.orderKey < newOrderKey)
    ) {
      return;
    }
    setOptions((prev) => {
      const target = prev.find((opt) => opt.typeId === typeId);
      if (target == null) {
        throw new Error('Can not find merchant order item with id: ' + typeId);
      }
      const swapWith = prev.find((opt) => opt.orderKey === newOrderKey);
      return prev.map((opt) => {
        if (opt.typeId === target.typeId) {
          return { ...opt, orderKey: newOrderKey };
        }
        if (swapWith != null && opt.typeId === swapWith.typeId) {
          return { ...opt, orderKey: target.orderKey };
        }
        return opt;
      });
    });
  }

  function resetOption(): void {
    if (editingOption == null) return;
    const defaultOption = options.find((opt) => opt.typeId === 0);
    if (defaultOption == null) {
      throw Error('No default option for containers found');
    }
    const reset = { ...editingOption };
    if (containerTypeClassesById[editingOption.typeId] === 'ProductPortfolio') {
      reset.pickupDisabled = defaultOption.pickupDisabled;
      reset.resupplyDisabled = defaultOption.resupplyDisabled;
      reset.resupplyDisabledUntil = defaultOption.resupplyDisabledUntil;
      reset.resupplyOptions = defaultOption.resupplyOptions;
    } else {
      reset.pickupDisabled = true;
      reset.resupplyDisabled = true;
      reset.resupplyDisabledUntil = null;
      reset.resupplyOptions = [];
    }
    setEditingOption(reset);
  }

  async function save(): Promise<void> {
    setSaving(true);
    try {
      await Communicator.PostData(
        '/MerchantOrder/SetMerchantOrderItemOptions',
        options.map((opt) => {
          const namesByLanguage: { [key: number]: string } = {};
          for (const key of Object.keys(UserLanguage)) {
            const name = opt.namesByLanguage?.[key];
            // Never send null names: they would be stored as-is and come back out
            // of TranslateName as null, spreading the problem below to more rows.
            if (name != null && name !== '') {
              namesByLanguage[IdByUserLanguage[key]] = name;
            }
          }
          return {
            ...opt,
            name: nameForPayload(opt),
            namesByLanguage,
            purchasePriceCents: parseInt(opt.purchasePriceCents),
          };
        }),
      );
      await fetchOptions();
      toast.success(i18n.t('MerchantOrder:optionsSaved'));
    } catch (error) {
      toast.error(i18n.t('MerchantOrder:optionsSaveError'), {
        description: await readApiErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  }

  // ── Cell renderers ───────────────────────────────────────────────────────--

  function renderResupplyOptions(values: number[]) {
    if (values.length === 0) {
      return <span className="tw:text-muted-foreground">—</span>;
    }
    const sorted = [...values].sort((a, b) => a - b);
    const shown = sorted.slice(0, 8);
    return (
      <div className="tw:flex tw:flex-wrap tw:gap-1">
        {shown.map((value) => (
          <Badge key={value} variant="secondary" className="tw:font-normal">
            {value}
          </Badge>
        ))}
        {sorted.length > shown.length && (
          <Badge variant="outline" className="tw:font-normal">
            +{sorted.length - shown.length}
          </Badge>
        )}
      </div>
    );
  }

  const activeBadge = (
    <Badge variant="success" className="tw:font-normal">
      {i18n.t('Common:active')}
    </Badge>
  );
  const deactivatedBadge = (
    <Badge variant="secondary" className="tw:font-normal">
      {i18n.t('MerchantOrder:deactivated')}
    </Badge>
  );

  function renderResupplyStatus(opt: MerchantOrderItemOption) {
    if (opt.resupplyDisabled) return deactivatedBadge;
    if (opt.resupplyDisabledUntil && isPauseActive(opt.resupplyDisabledUntil)) {
      return (
        <Badge variant="warning" className="tw:font-normal">
          {i18n.t('MerchantOrder:pausedUntil', {
            date: formatDate(opt.resupplyDisabledUntil),
          })}
        </Badge>
      );
    }
    return activeBadge;
  }

  const sortedOptions = [...options].sort((a, b) => b.orderKey - a.orderKey);
  const filteredOptions = sortedOptions.filter((opt) => {
    if (statusFilter === 'active' && !isOptionActive(opt)) return false;
    if (statusFilter === 'inactive' && isOptionActive(opt)) return false;
    return matchesSearch(opt, query);
  });
  const isContainer = (editingOption?.typeId ?? 0) > 0;
  const priceCents = parseInt(editingOption?.purchasePriceCents ?? '', 10);

  return (
    <div className="tw:flex tw:flex-col tw:gap-4 tw:p-6 tw:max-w-5xl">
      <div className="tw:flex tw:items-start tw:justify-between tw:gap-4">
        <div>
          <h2 className="tw:text-xl tw:font-medium">
            {i18n.t('MerchantOrder:itemOptionsTitle')}
          </h2>
          <p className="tw:mt-1 tw:text-sm tw:text-muted-foreground">
            {i18n.t('MerchantOrder:itemOptionsIntro')}
          </p>
        </div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
          <Button variant="outline" onClick={addOption} disabled={saving}>
            <Plus className="tw:size-4" />
            {i18n.t('MerchantOrder:addOption')}
          </Button>
          <Button onClick={save} disabled={!isDirty || saving}>
            {saving && <Spinner />}
            {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
          </Button>
        </div>
      </div>

      {isDirty && (
        <p className="tw:text-sm tw:text-amber-600">
          {i18n.t('MerchantOrder:unsavedChanges')}
        </p>
      )}

      {sortedOptions.length === 0 ? (
        <Empty>
          <EmptyTitle>{i18n.t('MerchantOrder:noOptions')}</EmptyTitle>
          <EmptyDescription>
            {i18n.t('MerchantOrder:noOptionsHint')}
          </EmptyDescription>
        </Empty>
      ) : (
        <>
          <div className="tw:flex tw:items-center tw:gap-2">
            <div className="tw:relative tw:max-w-sm tw:flex-1">
              <Search className="tw:pointer-events-none tw:absolute tw:left-2.5 tw:top-1/2 tw:size-4 tw:-translate-y-1/2 tw:text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={i18n.t('MerchantOrder:searchByNameOrId')}
                className="tw:pl-8"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger
                className="tw:w-[160px]"
                aria-label={i18n.t('MerchantOrder:statusFilterInfo')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">
                  {i18n.t('Common:active')}
                </SelectItem>
                <SelectItem value="inactive">
                  {i18n.t('MerchantOrder:deactivated')}
                </SelectItem>
                <SelectItem value="all">
                  {i18n.t('MerchantOrder:all')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Card className="tw:p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="tw:h-11 tw:w-[70px] tw:pl-4">
                    {i18n.t('Common:id')}
                  </TableHead>
                  <TableHead>{i18n.t('Common:name')}</TableHead>
                  <TableHead>
                    {i18n.t('MerchantOrder:resupplyOptions')}
                  </TableHead>
                  <TableHead className="tw:w-[150px]">
                    {i18n.t('MerchantOrder:resupply')}
                  </TableHead>
                  <TableHead className="tw:w-[120px]">
                    {i18n.t('MerchantOrder:pickup')}
                  </TableHead>
                  <TableHead className="tw:w-[120px] tw:pr-3 tw:text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOptions.length === 0 && (
                  <TableRow className="tw:hover:bg-transparent">
                    <TableCell
                      colSpan={6}
                      className="tw:py-8 tw:text-center tw:text-sm tw:text-muted-foreground"
                    >
                      {i18n.t('MerchantOrder:noOptionsMatch')}
                    </TableCell>
                  </TableRow>
                )}
                {filteredOptions.map((item) => {
                  const reorderable = item.typeId <= 0;
                  return (
                    <TableRow
                      key={item.typeId}
                      onClick={() => editOption(item)}
                      className="tw:cursor-pointer"
                    >
                      <TableCell className="tw:pl-4 tw:font-mono tw:text-xs tw:text-muted-foreground">
                        {item.typeId}
                      </TableCell>
                      <TableCell className="tw:font-medium">
                        {item.name}
                      </TableCell>
                      <TableCell>
                        {renderResupplyOptions(item.resupplyOptions)}
                      </TableCell>
                      <TableCell>{renderResupplyStatus(item)}</TableCell>
                      <TableCell>
                        {item.pickupDisabled ? deactivatedBadge : activeBadge}
                      </TableCell>
                      <TableCell className="tw:pr-3">
                        <div className="tw:flex tw:items-center tw:justify-end tw:gap-1">
                          {reorderable && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={i18n.t('Common:moveUp')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOrderKey(item.typeId, item.orderKey + 1);
                                }}
                              >
                                <ArrowUp className="tw:size-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                title={i18n.t('Common:moveDown')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOrderKey(item.typeId, item.orderKey - 1);
                                }}
                              >
                                <ArrowDown className="tw:size-4" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={i18n.t('Common:edit')}
                            onClick={(e) => {
                              e.stopPropagation();
                              editOption(item);
                            }}
                          >
                            <Pencil className="tw:size-4 tw:text-muted-foreground" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Dialog
        open={editingOption != null}
        onOpenChange={(open) => !open && setEditingOption(null)}
      >
        <DialogContent className="tw:max-h-[85vh] tw:max-w-lg tw:overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{i18n.t('MerchantOrder:editOption')}</DialogTitle>
            {isContainer && (
              <DialogDescription>
                {i18n.t('MerchantOrder:containerReadonlyNote')}
              </DialogDescription>
            )}
          </DialogHeader>

          {editingOption && (
            <div className="tw:flex tw:flex-col tw:gap-4 tw:py-1">
              {/* ── Names ─────────────────────────────────────────────────── */}
              <div className="tw:flex tw:flex-col tw:gap-1.5">
                <Label htmlFor="internal-name">
                  {i18n.t('Common:internalName')}
                </Label>
                <Input
                  id="internal-name"
                  value={editingOption.internalName}
                  disabled={isContainer}
                  className={cn(isContainer && 'tw:bg-muted')}
                  onChange={(e) => renameOption(e.target.value)}
                />
              </div>

              {editingOption.namesByLanguage && (
                <Collapsible open={namesOpen} onOpenChange={setNamesOpen}>
                  <CollapsibleTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="tw:w-full tw:justify-between tw:px-0 tw:hover:bg-transparent"
                    >
                      <span className="tw:text-sm tw:font-medium">
                        {i18n.t('MerchantOrder:displayNames')}
                      </span>
                      <ChevronDown
                        className={cn(
                          'tw:size-4 tw:text-muted-foreground tw:transition-transform',
                          namesOpen && 'tw:rotate-180',
                        )}
                      />
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="tw:grid tw:grid-cols-2 tw:gap-3 tw:pt-2">
                      {Object.keys(UserLanguage).map((lang) => (
                        <div
                          key={lang}
                          className="tw:flex tw:flex-col tw:gap-1.5"
                        >
                          <Label
                            htmlFor={`name-${lang}`}
                            className="tw:text-xs tw:text-muted-foreground"
                          >
                            {lang}
                          </Label>
                          <Input
                            id={`name-${lang}`}
                            value={editingOption.namesByLanguage[lang] ?? ''}
                            disabled={isContainer}
                            className={cn(isContainer && 'tw:bg-muted')}
                            onChange={(e) =>
                              setEditingOption({
                                ...editingOption,
                                namesByLanguage: {
                                  ...editingOption.namesByLanguage,
                                  [lang]: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* ── Purchase price ────────────────────────────────────────── */}
              <div className="tw:flex tw:flex-col tw:gap-1.5">
                <Label htmlFor="purchase-price">
                  {i18n.t('MerchantOrder:purchasePrice')}
                </Label>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Input
                    id="purchase-price"
                    inputMode="numeric"
                    value={editingOption.purchasePriceCents}
                    onChange={(e) =>
                      setEditingOption({
                        ...editingOption,
                        purchasePriceCents: e.target.value.replace(/\D+/, ''),
                      })
                    }
                  />
                  <span className="tw:w-20 tw:shrink-0 tw:text-right tw:text-sm tw:text-muted-foreground">
                    {!isNaN(priceCents) &&
                      centsToPrice(priceCents, Currency.EUR)}
                  </span>
                </div>
              </div>

              {/* ── Resupply ──────────────────────────────────────────────── */}
              <div className="tw:flex tw:flex-col tw:gap-3 tw:rounded-md tw:border tw:p-3">
                <div className="tw:flex tw:items-center tw:justify-between">
                  <Label htmlFor="resupply-disabled" className="tw:font-normal">
                    {i18n.t('MerchantOrder:resupplyDisabled')}
                  </Label>
                  <Switch
                    id="resupply-disabled"
                    checked={!!editingOption.resupplyDisabled}
                    onCheckedChange={(checked) =>
                      setEditingOption({
                        ...editingOption,
                        resupplyDisabled: checked,
                      })
                    }
                  />
                </div>

                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <Label htmlFor="resupply-until">
                    {i18n.t('MerchantOrder:resupplyDisabledUntil')}
                  </Label>
                  <Input
                    id="resupply-until"
                    type="date"
                    value={
                      editingOption.resupplyDisabledUntil?.substring(0, 10) ||
                      ''
                    }
                    onChange={(e) =>
                      setEditingOption({
                        ...editingOption,
                        resupplyDisabledUntil: e.target.value || null,
                      })
                    }
                  />
                </div>

                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <div className="tw:flex tw:items-center tw:justify-between">
                    <Label>{i18n.t('MerchantOrder:resupplyOptions')}</Label>
                    <span className="tw:text-xs tw:text-muted-foreground">
                      {i18n.t('MerchantOrder:optionsSelected', {
                        count: editingOption.resupplyOptions.length,
                      })}
                    </span>
                  </div>
                  <div className="tw:flex tw:max-h-44 tw:flex-wrap tw:gap-1.5 tw:overflow-y-auto tw:rounded-md tw:border tw:bg-muted/30 tw:p-2">
                    {Array.from(
                      new Set([
                        ...RESUPPLY_OPTIONS,
                        ...editingOption.resupplyOptions,
                      ]),
                    )
                      .sort((a, b) => a - b)
                      .map((value) => {
                        const selected =
                          editingOption.resupplyOptions.includes(value);
                        return (
                          <Button
                            key={value}
                            type="button"
                            size="xs"
                            variant={selected ? 'default' : 'outline'}
                            className="tw:min-w-9"
                            onClick={() => toggleResupplyOption(value)}
                          >
                            {value}
                          </Button>
                        );
                      })}
                  </div>
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <Input
                      type="number"
                      min={0}
                      step={1}
                      inputMode="numeric"
                      className="tw:w-40"
                      placeholder={i18n.t(
                        'MerchantOrder:customResupplyQuantity',
                      )}
                      value={customQuantity}
                      onChange={(e) => setCustomQuantity(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCustomResupplyOption();
                        }
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={customQuantity.trim() === ''}
                      onClick={addCustomResupplyOption}
                    >
                      {i18n.t('MerchantOrder:addQuantity')}
                    </Button>
                  </div>
                </div>
              </div>

              {/* ── Pickup ────────────────────────────────────────────────── */}
              <div className="tw:flex tw:items-center tw:justify-between tw:rounded-md tw:border tw:p-3">
                <Label htmlFor="pickup-disabled" className="tw:font-normal">
                  {i18n.t('MerchantOrder:pickupDisabled')}
                </Label>
                <Switch
                  id="pickup-disabled"
                  checked={!!editingOption.pickupDisabled}
                  onCheckedChange={(checked) =>
                    setEditingOption({
                      ...editingOption,
                      pickupDisabled: checked,
                    })
                  }
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {isContainer && (
              <Button
                variant="outline"
                className="tw:mr-auto"
                onClick={resetOption}
              >
                {i18n.t('Common:reset')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditingOption(null)}>
              {i18n.t('Common:cancel')}
            </Button>
            <Button onClick={confirmEdit}>{i18n.t('Common:confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
